import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { CheckIcon, FailedIcon, RefundedIcon, SkippedIcon } from '@arcade/web/icons/react'
import { FormBlockerElementTimelineElementEventIcon } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

export const BORDER_WIDTH = '0.25rem'
export const CIRCLE_SIZE = '1.5rem'

type IconColorProperties = {
  background: string
  stroke: string
  iconColor: string
  color: string
  opacity?: number
}

const TimelineEventWrapper = styled.div`
  position: relative;
`

const Circle = css`
  width: ${CIRCLE_SIZE};
  height: ${CIRCLE_SIZE};
  border-radius: 50%;
`

const Icon = styled.div<{ iconColorProperties: IconColorProperties }>`
  border: ${BORDER_WIDTH} solid ${props => props.iconColorProperties.stroke};
  color: ${props => props.iconColorProperties.iconColor};
  ${Circle}
`

const IconBackground = styled.div<{ background: string; opacity?: number }>`
  background: ${props => props.background};
  opacity: ${props => props.opacity ?? 1};
  ${Circle}
  position: absolute;
  top: 0;
  z-index: -1;
`

const useGetIconColorProperties = ({
  icon,
}: {
  icon: FormBlockerElementTimelineElementEventIcon
}): IconColorProperties => {
  const { arcadeColors } = useTheme()
  let background = ''
  let stroke = ''
  const iconColor = arcadeColors.semantic.iconInverse
  const color = ''
  let opacity
  if (icon === FormBlockerElementTimelineElementEventIcon.HIGHLIGHT) {
    background = arcadeColors.semantic.iconBrand
    stroke = arcadeColors.semantic.iconBrand
    opacity = 0.2
  } else if (icon === FormBlockerElementTimelineElementEventIcon.NORMAL) {
    background = arcadeColors.semantic.iconInverse
    stroke = arcadeColors.semantic.iconExtraSubtle
  } else if (
    icon === FormBlockerElementTimelineElementEventIcon.COMPLETED ||
    icon === FormBlockerElementTimelineElementEventIcon.SKIPPED ||
    icon === FormBlockerElementTimelineElementEventIcon.MISSED ||
    icon === FormBlockerElementTimelineElementEventIcon.REFUNDED
  ) {
    background = arcadeColors.semantic.iconExtraSubtle
    stroke = arcadeColors.semantic.iconExtraSubtle
  } else if (icon === FormBlockerElementTimelineElementEventIcon.HIGHLIGHT_COMPLETED) {
    background = arcadeColors.semantic.iconBrand
    stroke = arcadeColors.semantic.iconBrand
  } else if (icon === FormBlockerElementTimelineElementEventIcon.ALERT) {
    background = arcadeColors.semantic.iconDanger
    stroke = arcadeColors.semantic.iconDanger
    opacity = 0.2
  } else if (icon === FormBlockerElementTimelineElementEventIcon.FAILED) {
    background = arcadeColors.semantic.iconDanger
    stroke = arcadeColors.semantic.iconDanger
  }

  return {
    background,
    stroke,
    iconColor,
    color,
    opacity,
  }
}

const getIcon = ({ icon }: { icon: FormBlockerElementTimelineElementEventIcon }) => {
  if (icon === FormBlockerElementTimelineElementEventIcon.COMPLETED) {
    return <CheckIcon />
  } else if (icon === FormBlockerElementTimelineElementEventIcon.SKIPPED) {
    return <SkippedIcon />
  } else if (
    icon === FormBlockerElementTimelineElementEventIcon.MISSED ||
    icon === FormBlockerElementTimelineElementEventIcon.FAILED
  ) {
    return <FailedIcon />
  } else if (icon === FormBlockerElementTimelineElementEventIcon.HIGHLIGHT_COMPLETED) {
    return <CheckIcon />
  } else if (icon === FormBlockerElementTimelineElementEventIcon.REFUNDED) {
    return <RefundedIcon />
  }
  return
}

export const TimelineEventIcon = ({ icon }: { icon: FormBlockerElementTimelineElementEventIcon }) => {
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const iconColorProperties = useGetIconColorProperties({ icon })
  const iconEl = getIcon({ icon })

  if (isArcadeMigrationEnabled) return iconEl

  return (
    <TimelineEventWrapper>
      <Icon iconColorProperties={iconColorProperties}>{iconEl}</Icon>
      <IconBackground background={iconColorProperties.background} opacity={iconColorProperties.opacity} />
    </TimelineEventWrapper>
  )
}
